import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

import { ToolName } from '../../Types/enums';
import { ToolConfig } from '../../util/tools';
import { useFeatures } from '../Providers/FeatureProvider';

export type ToolsSettingDialogProps = {
  tools: ToolName[];
  disabledTools: ToolName[];
  handleSave: (toolNames: ToolName[]) => void;
  handleClose: () => void;
};

const ToolsSettingDialog = ({
  tools,
  disabledTools = [],
  handleSave,
  handleClose,
}: ToolsSettingDialogProps): JSX.Element => {
  const features = useFeatures();
  const [selectedTools, setSelectedTools] = useState<ToolName[]>(tools);

  const toggleTool = useCallback(
    (toolName: ToolName) => {
      if (disabledTools.includes(toolName)) return;
      setSelectedTools((prev) =>
        prev.includes(toolName) ? prev.filter((t) => t !== toolName) : [...prev, toolName]
      );
    },
    [disabledTools]
  );

  const excludedTools = useMemo(() => {
    const retExcludedTools = [ToolName.WEB_SEARCH, ToolName.DEEP_RESEARCH, ToolName.GLEAN_SEARCH];

    if (!features.stracl) {
      retExcludedTools.push(ToolName.STRACL_MCP);
    }

    return retExcludedTools;
  }, [features]);

  return (
    <Dialog open={true} onClose={handleClose}>
      <DialogTitle>Select Tools</DialogTitle>
      <DialogContent dividers>
        <List>
          {Object.values(ToolName)
            .filter((tool) => !excludedTools.includes(tool))
            .map((toolName) => (
              <ListItemButton
                key={toolName}
                onClick={() => toggleTool(toolName)}
                disabled={disabledTools.includes(toolName) || tools.includes(toolName)}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedTools.includes(toolName)}
                    tabIndex={-1}
                    disableRipple
                    disabled={disabledTools.includes(toolName) || tools.includes(toolName)}
                  />
                </ListItemIcon>
                <ListItemText primary={ToolConfig[toolName].name} />
              </ListItemButton>
            ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={() => {
            handleSave(selectedTools);
            handleClose();
          }}
          variant="contained"
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ToolsSettingDialog;
